import { type Dispatch, type SetStateAction, useEffect, useRef, useState } from "react";

type ContainerId = string | number;
// `undefined` means the drag ended off a list and every local copy should snap back.
type Listener = (hoveredContainerId: ContainerId | undefined) => void;
type DraggedItem = { id: string; order: number } & Record<string, unknown>;
type DragSnapshot = {
  item: DraggedItem;
  order: number;
  typeAttribute?: string;
  typeValue: unknown;
};

let hoveredContainerId: ContainerId | undefined;
let dragSnapshot: DragSnapshot | undefined;
// Set only while a committed drop is telling other lists to drop the dragged page.
let successfullyDraggedItemId: string | undefined;
const listeners = new Set<Listener>();

const subscribe = (listener: Listener) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

const notify = (containerId: ContainerId | undefined) => {
  listeners.forEach((listener) => listener(containerId));
};

/** First hover wins. Later hovers mutate `order` and the list id; cancel restores this. */
export const rememberDragItem = (item: { id: string; order: number }, typeAttribute?: string) => {
  const record = item as DraggedItem;
  if (dragSnapshot?.item === record) return;

  dragSnapshot = {
    item: record,
    order: item.order,
    typeAttribute,
    typeValue: typeAttribute ? record[typeAttribute] : undefined,
  };
};

const restoreDragItem = () => {
  if (!dragSnapshot) return;

  const { item, order, typeAttribute, typeValue } = dragSnapshot;
  item.order = order;

  if (typeAttribute) {
    item[typeAttribute] = typeValue;
  }

  dragSnapshot = undefined;
};

/** Tell subscribers the pointer moved, so other lists can drop a preview and hide the row it left. */
export const hoverDragContainer = (containerId: ContainerId) => {
  if (hoveredContainerId === containerId) return;

  hoveredContainerId = containerId;
  notify(containerId);
};

/**
 * A drop on a list keeps the preview. A drop anywhere else restores every list
 * and the dragged item.
 */
export const endDragPreview = (committed: boolean) => {
  if (committed) {
    // The drag has ended, so lists the pointer left can unmount the page. Until the
    // store refresh arrives, dropping it here is what keeps it from popping back.
    successfullyDraggedItemId = dragSnapshot?.item.id;
    const visibleId = hoveredContainerId;
    dragSnapshot = undefined;
    hoveredContainerId = undefined;

    // Snapshot and hovered id are cleared first, so row listeners unhide in this same pass
    // while list listeners still see `successfullyDraggedItemId` and drop the page.
    if (visibleId !== undefined) notify(visibleId);
    successfullyDraggedItemId = undefined;
  } else {
    restoreDragItem();
    hoveredContainerId = undefined;
    notify(undefined);
  }
};

/**
 * Keep a list's local copy in sync with a drag.
 * Passing `containerId` also drops a preview when the pointer moves to another list.
 * Every list snaps back to `source` when the drag ends off a list.
 */
export const useDragListReset = <T>(
  source: T,
  setLocal: Dispatch<SetStateAction<T>>,
  containerId?: string,
) => {
  const sourceRef = useRef(source);
  const setLocalRef = useRef(setLocal);

  // The drag listener is created once. Refresh the refs after commit so it sees the
  // latest list without writing them during render.
  useEffect(() => {
    sourceRef.current = source;
    setLocalRef.current = setLocal;
  });

  useEffect(
    () =>
      subscribe((hoveredId) => {
        const dragEndedOffList = hoveredId === undefined;
        const pointerLeftThisList = containerId !== undefined && hoveredId !== containerId;

        if (!dragEndedOffList && !pointerLeftThisList) return;

        const sourceList = sourceRef.current;
        if (successfullyDraggedItemId && Array.isArray(sourceList)) {
          const draggedId = successfullyDraggedItemId;
          const containsDragged = sourceList.some(
            (entry) =>
              !!entry && typeof entry === "object" && "id" in entry && entry.id === draggedId,
          );

          if (containsDragged) {
            setLocalRef.current(
              sourceList.filter(
                (entry) =>
                  !(entry && typeof entry === "object" && "id" in entry && entry.id === draggedId),
              ) as T,
            );
            return;
          }
        }

        setLocalRef.current(sourceRef.current);
      }),
    [containerId],
  );
};

/**
 * The activity a page was dragged out of keeps that page in its local list so the drag
 * source node stays mounted. Collapse it while the pointer is over a different list.
 */
export const useHideDraggedRow = (itemId?: string, containerId?: ContainerId) => {
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    const update = () => {
      const draggedId = dragSnapshot?.item.id;
      const next = Boolean(
        draggedId &&
          itemId &&
          draggedId === itemId &&
          containerId !== undefined &&
          hoveredContainerId !== undefined &&
          hoveredContainerId !== containerId,
      );
      setHidden((current) => (current === next ? current : next));
    };

    const unsubscribe = subscribe(() => update());
    // A row mounted after the hover already happened (the preview) still has to read current state.
    update();
    return unsubscribe;
  }, [itemId, containerId]);

  return hidden;
};
